/**
 * help/registry — the single list of every help article, plus lookup helpers.
 *
 * ADDING AN ARTICLE: write it into the file for its category under articles/,
 * and it appears here automatically. `registry.test.ts` will fail the build if
 * the slug collides, a `related` slug does not exist, or a screenshot path is
 * not on disk — so a typo cannot ship as a dead link.
 */

import { HELP_CATEGORIES, type HelpArticle, type HelpCategoryId } from './types';
import { startArticles } from './articles/start';
import { basicsArticles } from './articles/basics';
import { setupArticles } from './articles/setup';
import { appealsArticles } from './articles/appeals';
import { valuesArticles, billingArticles } from './articles/money';
import { taxBillArticles, docsArticles } from './articles/operations';
import { peopleArticles, reportsArticles, adminArticles } from './articles/admin';

export const HELP_ARTICLES: HelpArticle[] = [
  ...startArticles,
  ...basicsArticles,
  ...setupArticles,
  ...appealsArticles,
  ...valuesArticles,
  ...taxBillArticles,
  ...billingArticles,
  ...docsArticles,
  ...peopleArticles,
  ...reportsArticles,
  ...adminArticles,
];

const BY_SLUG = new Map(HELP_ARTICLES.map((a) => [a.slug, a]));

export function getArticle(slug: string | undefined): HelpArticle | undefined {
  return slug ? BY_SLUG.get(slug) : undefined;
}

export function articlesInCategory(id: HelpCategoryId): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.category === id);
}

/** Categories paired with their articles, in declared display order. */
export function categorizedArticles(): { id: HelpCategoryId; label: string; blurb: string; articles: HelpArticle[] }[] {
  return HELP_CATEGORIES.map((c) => ({
    id: c.id,
    label: c.label,
    blurb: c.blurb,
    articles: articlesInCategory(c.id),
  }));
}

/** The display metadata for a category id. */
export function categoryMeta(id: HelpCategoryId) {
  return HELP_CATEGORIES.find((c) => c.id === id);
}

/** Every article in reading order: categories in display order, articles within. */
function readingOrder(): HelpArticle[] {
  return HELP_CATEGORIES.flatMap((c) => articlesInCategory(c.id));
}

/**
 * The articles either side of `slug` in overall reading order, plus the ones in
 * its own category for the section rail.
 *
 * Prev/next RUN ON past a category boundary rather than stopping at it, so
 * somebody reading the whole guide never hits a dead end. The caller is told
 * when a step crosses into a new section (`nextCrossesSection`) and labels it,
 * because arriving in a different subject unannounced is the non-sequitur —
 * not the crossing itself.
 */
export function articleNeighbours(slug: string): {
  prev?: HelpArticle;
  next?: HelpArticle;
  prevCrossesSection: boolean;
  nextCrossesSection: boolean;
  siblings: HelpArticle[];
} {
  const article = getArticle(slug);
  if (!article) return { siblings: [], prevCrossesSection: false, nextCrossesSection: false };

  const all = readingOrder();
  const i = all.findIndex((a) => a.slug === slug);
  const prev = i > 0 ? all[i - 1] : undefined;
  const next = i >= 0 && i < all.length - 1 ? all[i + 1] : undefined;

  return {
    prev,
    next,
    prevCrossesSection: !!prev && prev.category !== article.category,
    nextCrossesSection: !!next && next.category !== article.category,
    siblings: articlesInCategory(article.category),
  };
}

/**
 * The reading order we put in front of somebody on day one. Deliberately short:
 * a "start here" list of twenty items is a list nobody starts.
 */
export const HELP_ESSENTIALS: string[] = [
  'what-is-appealstack',
  'the-big-picture',
  'find-your-way-around',
  'your-dashboard',
  'what-is-an-appeal',
  'searching-for-anything',
];

/**
 * Which article a given app route should offer as its contextual help.
 *
 * Longest prefix wins, so '/documents/review-queue' resolves to the review-queue
 * article rather than the general documents one. Order in this table does not
 * matter — the lookup sorts by specificity, so adding a route later cannot
 * silently shadow an existing one.
 */
const ROUTE_HELP: Record<string, string> = {
  '/': 'your-dashboard',
  '/calendar': 'the-calendar',
  '/appeals/queues': 'the-appeals-queue',
  '/clients/new': 'adding-a-client',
  '/clients': 'adding-a-client',
  '/parent-clients': 'parent-clients',
  '/properties/new': 'adding-a-property',
  '/properties': 'adding-a-property',
  '/parcels/new': 'adding-a-parcel',
  '/parcels/exemption-proposals': 'mass-updates',
  '/parcels': 'what-is-a-parcel-value',
  '/tax-bills': 'tax-bills',
  '/filed-returns': 'returns',
  '/assessors': 'who-does-what-outside-your-firm',
  '/collectors': 'who-does-what-outside-your-firm',
  '/jurisdictions': 'who-does-what-outside-your-firm',
  '/invoices': 'invoices',
  '/invoice-items': 'invoice-items',
  '/invoice-terms': 'fee-agreements',
  '/consultant-deals': 'consultant-deals',
  '/quickbooks': 'quickbooks',
  '/contacts': 'contacts',
  '/documents/review-queue': 'document-review-queue',
  '/documents': 'documents',
  '/information-requests': 'information-requests',
  '/notifications': 'notifications',
  '/activity-log': 'activity-and-audit-logs',
  '/audit-logs': 'activity-and-audit-logs',
  '/user-management': 'users-and-roles',
  '/org-settings': 'org-settings',
  '/custom-fields': 'custom-fields',
  '/state-rules': 'state-rules',
  '/forms': 'forms-library',
  '/imports': 'importing-from-a-spreadsheet',
  '/year-roll': 'year-roll',
  '/reports': 'reports-overview',
  '/report-builder': 'report-builder',
};

/** Route prefixes, longest first — computed once so lookup is a simple scan. */
const ROUTE_KEYS = Object.keys(ROUTE_HELP).sort((a, b) => b.length - a.length);

/**
 * The help slug that best fits `pathname`, or undefined if we have nothing
 * specific to offer (in which case the caller should open the Help Center index
 * rather than guessing at an article).
 */
export function helpForRoute(pathname: string): string | undefined {
  if (pathname === '/') return ROUTE_HELP['/'];
  for (const key of ROUTE_KEYS) {
    if (key === '/') continue;
    if (pathname === key || pathname.startsWith(key + '/')) return ROUTE_HELP[key];
  }
  return undefined;
}
